import { assign } from "radash";
import { type StateCreator } from "zustand";

import { twoFactorService } from "../../api/twoFactor";
import { TProfileData, TRole, TUpdateProfileRequestPayload, TUser, TUserToken, userService } from "../../api/user";
import { verificationService } from "../../api/verification";
import { SESSION_EXPIRED_USER_MESSAGE } from "../../utils/authUtils";
import { tokenStorage } from "../../utils/tokenStorage";
import { extractErrorMessage, isSessionExpiredCode } from "../../utils/errorUtils";
import { getJwtExpiration } from "../../utils/jwt";

export type TVerificationOtpMethod = "email" | "phone";

// Shared timing constants (mirrors backend defaults)
const OTP_EXPIRY_MS = 10 * 60 * 1000;   // 10 min — matches EMAIL/PHONE_OTP_EXPIRY_MINUTES
const RESEND_COOLDOWN_MS = 60 * 1000;    // 60 s  — matches *_RESEND_COOLDOWN_SECONDS

export type TAuthStoreState = {
  user: TUser | null;
  profile: TProfileData | null;
  roles: TRole[] | null;
  resetToken: string | null;
  isRefreshing: boolean;
  isProfileLoading: boolean;
  profileError: string | null;
  isRolesLoading: boolean;
  rolesError: string | null;
  isUpdatingProfile: boolean;
  updateProfileError: string | null;
  // Verification OTP (post-auth verification flow; transient)
  isSendingVerificationOtp: boolean;
  isResendingVerificationOtp: boolean;
  isVerifyingVerificationOtp: boolean;
  isChangingVerificationContact: boolean;
  verificationOtpMethod: TVerificationOtpMethod | null;
  verificationOtpSentAt: number | null;
  verificationOtpExpiresAt: number | null;
  verificationOtpResendAvailableAt: number | null;
  verificationOtpFailedAttempts: number;
  /** Correlation ID returned by the backend for the latest OTP send/resend.
   *  Used to poll delivery status via GET /verification/{id}/status. */
  verificationRequestId: string | null;
  /** New email address pending OTP confirmation during Settings email-change flow. */
  pendingEmailChange: string | null;
} & Partial<TUserToken>;

export type TUpdateProfileResult = {
  success: boolean;
  error?: string;
  errorObject?: unknown;
};

export type TRequestPhoneNumberChangeResult = {
  success: boolean;
  message?: string;
  error?: string;
  errorObject?: unknown;
};

export type TRequestEmailChangeResult = {
  success: boolean;
  message?: string;
  error?: string;
  errorObject?: unknown;
};

export type TVerifyPhoneOtpResult = {
  success: boolean;
  verified?: boolean;
  message?: string;
  error?: string;
  errorObject?: unknown;
};

export type TResendPhoneVerificationResult = {
  success: boolean;
  message?: string;
  error?: string;
  errorObject?: unknown;
};

export type TSendVerificationOtpResult = {
  success: boolean;
  message?: string;
  sessionExpired?: boolean;
  error?: string;
  errorObject?: unknown;
};

export type TResendVerificationOtpResult = {
  success: boolean;
  message?: string;
  otpReset?: boolean;
  sessionExpired?: boolean;
  error?: string;
  errorObject?: unknown;
};

export type TVerifyVerificationOtpResult = {
  success: boolean;
  verified?: boolean;
  alreadyVerified?: boolean;
  otpReset?: boolean;
  sessionExpired?: boolean;
  attemptsRemaining?: number;
  message?: string;
  error?: string;
  errorObject?: unknown;
};

export type TChangeVerificationContactResult = {
  success: boolean;
  contactUpdated?: boolean;
  message?: string;
  sessionExpired?: boolean;
  error?: string;
  errorObject?: unknown;
};

export type TVerifyTwoFactorLoginResult = {
  success: boolean;
  roles?: TRole[] | null;
  error?: string;
  errorObject?: unknown;
};

export type TAuthStoreActions = {
  updateAuthStore: (payload: RecursivePartial<TAuthStoreState>) => void;
  setResetToken: (token: string | null) => void;
  setIsRefreshing: (isRefreshing: boolean) => void;
  resetAuthStore: () => void;
  fetchProfile: (options?: { force?: boolean }) => Promise<void>;
  fetchRoles: (options?: { force?: boolean }) => Promise<void>;
  updateProfile: (payload: TUpdateProfileRequestPayload) => Promise<TUpdateProfileResult>;
  requestPhoneNumberChange: (phoneNumber: string) => Promise<TRequestPhoneNumberChangeResult>;
  requestEmailChange: (newEmail: string) => Promise<TRequestEmailChangeResult>;
  verifyPhoneOtp: (otp: string) => Promise<TVerifyPhoneOtpResult>;
  resendPhoneVerification: () => Promise<TResendPhoneVerificationResult>;
  sendVerificationOtp: (method: TVerificationOtpMethod) => Promise<TSendVerificationOtpResult>;
  resendVerificationOtp: (method: TVerificationOtpMethod) => Promise<TResendVerificationOtpResult>;
  verifyVerificationOtp: (method: TVerificationOtpMethod, otp: string) => Promise<TVerifyVerificationOtpResult>;
  /** Change the contact address during an active verification flow.
   *  Updates `profile.email` / `profile.phone_number` only when `contact_updated: true`. */
  changeVerificationEmail: (newEmail: string) => Promise<TChangeVerificationContactResult>;
  changeVerificationPhone: (newPhone: string) => Promise<TChangeVerificationContactResult>;
  verifyTwoFactorLogin: (token: string) => Promise<TVerifyTwoFactorLoginResult>;
};

const initialAuthStoreState: TAuthStoreState = {
  user: null,
  profile: null,
  roles: null,
  accessToken: undefined,
  expiresAt: undefined,
  resetToken: null,
  isRefreshing: false,
  isProfileLoading: false,
  profileError: null,
  isRolesLoading: false,
  rolesError: null,
  isUpdatingProfile: false,
  updateProfileError: null,
  // Verification OTP (post-auth verification flow; do not persist)
  isSendingVerificationOtp: false,
  isResendingVerificationOtp: false,
  isVerifyingVerificationOtp: false,
  isChangingVerificationContact: false,
  verificationOtpMethod: null,
  verificationOtpSentAt: null,
  verificationOtpExpiresAt: null,
  verificationOtpResendAvailableAt: null,
  verificationOtpFailedAttempts: 0,
  verificationRequestId: null,
  pendingEmailChange: null,
};

export type TAuthStore = TAuthStoreState & TAuthStoreActions;

// Helper

/** Clears all transient OTP fields after a successful verification or logout. */
function _clearOtpState() {
  return {
    verificationOtpMethod: null as TVerificationOtpMethod | null,
    verificationOtpSentAt: null as number | null,
    verificationOtpExpiresAt: null as number | null,
    verificationOtpResendAvailableAt: null as number | null,
    verificationOtpFailedAttempts: 0,
    verificationRequestId: null as string | null,
    pendingEmailChange: null as string | null,
  };
}

export const createAuthStore: StateCreator<TAuthStore> = (set, get) => ({
  ...initialAuthStoreState,
  updateAuthStore: (payload) => {
    // Sync auth tokens to cookies when they're updated
    if (payload.accessToken) {
      tokenStorage.setAccessToken(payload.accessToken);
    }
    if (payload.expiresAt) {
      tokenStorage.setExpiresAt(payload.expiresAt);
    }
    return set((state) => assign(state, payload));
  },
  setResetToken: (token) => set(() => ({ resetToken: token })),
  setIsRefreshing: (isRefreshing) => set(() => ({ isRefreshing })),
  resetAuthStore: () => {
    // Clear cookies first to ensure clean state
    tokenStorage.clearAuthCookies();
    set(initialAuthStoreState);
  },

  fetchProfile: async (options = {}) => {
    const { force = false } = options;
    const state = get();

    // Skip if already loading
    if (state.isProfileLoading) {
      return;
    }

    // Skip if profile exists and not forcing refresh
    if (!force && state.profile) {
      return;
    }

    // Skip if not authenticated - check cookies as single source of truth
    if (!tokenStorage.getAccessToken()) {
      return;
    }

    set({ isProfileLoading: true, profileError: null });

    try {
      const { data, error } = await userService.getProfile();

      if (error) {
        const errorMessage = await extractErrorMessage(error);
        set({
          isProfileLoading: false,
          profileError: errorMessage || "Failed to load profile",
        });
        return;
      }

      if (data) {
        set({
          profile: data,
          isProfileLoading: false,
          profileError: null,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load profile";
      set({
        isProfileLoading: false,
        profileError: errorMessage,
      });
    }
  },

  fetchRoles: async (options = {}) => {
    const { force = false } = options;
    const state = get();

    // Skip if roles exist and not forcing refresh
    if (!force && state.roles) {
      return;
    }

    // Dedup: skip if a fetch is already in flight
    if (state.isRolesLoading) {
      return;
    }

    // Skip if not authenticated - check cookies as single source of truth
    if (!tokenStorage.getAccessToken()) {
      return;
    }

    set({ isRolesLoading: true, rolesError: null });

    try {
      const { data, error } = await userService.getRoles();

      if (error) {
        const errorMessage = await extractErrorMessage(error);
        set({ isRolesLoading: false, rolesError: errorMessage || "Failed to load permissions" });
        return;
      }

      if (data) {
        set({ roles: data, isRolesLoading: false, rolesError: null });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load permissions";
      set({ isRolesLoading: false, rolesError: errorMessage });
    }
  },

  updateProfile: async (payload): Promise<TUpdateProfileResult> => {
    set({ isUpdatingProfile: true, updateProfileError: null });

    try {
      const { error } = await userService.updateUser(payload, {});

      if (error) {
        const errorMessage = await extractErrorMessage(error);
        set({
          isUpdatingProfile: false,
          updateProfileError: errorMessage || "Failed to update profile",
        });
        return { success: false, error: errorMessage || "Failed to update profile", errorObject: error };
      }

      // Always refetch profile after a successful update to avoid response shape mismatches
      // and to ensure verification flags (email_verified/phone_verified) are up-to-date.
      const refreshed = await userService.getProfile();
      if (refreshed.error) {
        const refreshedErrorMessage = await extractErrorMessage(refreshed.error);
        set({
          isUpdatingProfile: false,
          updateProfileError: refreshedErrorMessage || "Profile updated, but failed to refresh profile",
        });
        return { success: true };
      }

      if (refreshed.data) {
        set({
          profile: refreshed.data,
          isUpdatingProfile: false,
          updateProfileError: null,
        });
        return { success: true };
      }

      set({ isUpdatingProfile: false });
      return { success: false, error: "No data returned" };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update profile";
      set({
        isUpdatingProfile: false,
        updateProfileError: errorMessage,
      });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  requestPhoneNumberChange: async (phoneNumber): Promise<TRequestPhoneNumberChangeResult> => {
    try {
      const trimmed = phoneNumber.trim();
      if (!trimmed) {
        return { success: false, error: "Phone number is required" };
      }

      const { data, error } = await userService.requestPhoneNumberChange({ phone_number: trimmed });
      if (error) {
        const errorMessage = await extractErrorMessage(error);
        return {
          success: false,
          error: errorMessage || "Failed to request phone number change",
          errorObject: error,
        };
      }

      return {
        success: true,
        message: data?.message ?? data?.data?.message ?? "Verification code sent",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to request phone number change";
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  requestEmailChange: async (newEmail): Promise<TRequestEmailChangeResult> => {
    try {
      const trimmed = newEmail.trim();
      if (!trimmed) {
        return { success: false, error: "Email address is required" };
      }

      const { data, error } = await userService.requestEmailChange({ new_email: trimmed });
      if (error) {
        const errorMessage = await extractErrorMessage(error);
        return {
          success: false,
          error: errorMessage || "Failed to request email change",
          errorObject: error,
        };
      }

      const now = Date.now();
      set({
        pendingEmailChange: trimmed,
        verificationOtpMethod: "email",
        verificationOtpSentAt: now,
        verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
        verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
        verificationOtpFailedAttempts: 0,
      });

      return {
        success: true,
        message:
          data?.message ??
          data?.data?.message ??
          "If the email address can be used, a verification code has been sent.",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to request email change";
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  verifyPhoneOtp: async (otp): Promise<TVerifyPhoneOtpResult> => {
    try {
      const trimmed = otp.trim();
      if (!trimmed) {
        return { success: false, error: "OTP is required" };
      }

      const { data, error } = await verificationService.verifyPhone({ otp: trimmed });
      if (error) {
        const errorMessage = await extractErrorMessage(error);
        return { success: false, error: errorMessage || "Verification failed", errorObject: error };
      }

      const verified = Boolean(data?.success && data.verified);
      if (verified) {
        // Refresh profile so phone_verified is up-to-date.
        await get().fetchProfile({ force: true });
      }

      return {
        success: Boolean(data?.success),
        verified,
        message: data?.message,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Verification failed";
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  resendPhoneVerification: async (): Promise<TResendPhoneVerificationResult> => {
    try {
      const { data, error } = await verificationService.resendPhoneVerification();
      if (error) {
        const errorMessage = await extractErrorMessage(error);
        return { success: false, error: errorMessage || "Failed to resend code", errorObject: error };
      }

      return {
        success: Boolean(data?.success),
        message: data?.message ?? "Verification code resent",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to resend code";
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  sendVerificationOtp: async (method): Promise<TSendVerificationOtpResult> => {
    const now = Date.now();

    set({
      isSendingVerificationOtp: true,
      verificationOtpMethod: method,
    });

    try {
      const result =
        method === "email"
          ? await verificationService.sendEmailVerification()
          : await verificationService.sendPhoneVerification();

      if (result.error) {
        // SESSION_EXPIRED — let the caller show a toast then redirect
        if (await isSessionExpiredCode(result.error)) {
          set({ isSendingVerificationOtp: false });
          return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
        }

        const errorMessage = await extractErrorMessage(result.error);
        set({ isSendingVerificationOtp: false });
        return {
          success: false,
          error: errorMessage || "Failed to send verification code",
          errorObject: result.error,
        };
      }

      set({
        isSendingVerificationOtp: false,
        verificationOtpMethod: method,
        verificationOtpSentAt: now,
        verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
        verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
        verificationOtpFailedAttempts: 0,
        verificationRequestId: result.data?.verification_request_id ?? null,
      });

      return {
        success: true,
        message: result.data?.message ?? "Verification code sent",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to send verification code";
      set({ isSendingVerificationOtp: false });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  resendVerificationOtp: async (method): Promise<TResendVerificationOtpResult> => {
    const now = Date.now();

    const state = get();
    const availableAt = state.verificationOtpResendAvailableAt;
    if (availableAt && now < availableAt) {
      const seconds = Math.ceil((availableAt - now) / 1000);
      return { success: false, error: `Please wait ${seconds}s before resending.` };
    }

    set({
      isResendingVerificationOtp: true,
      verificationOtpMethod: method,
    });

    try {
      if (method === "email" && state.pendingEmailChange) {
        const { data, error } = await userService.requestEmailChange({
          new_email: state.pendingEmailChange,
        });

        if (error) {
          if (await isSessionExpiredCode(error)) {
            set({ isResendingVerificationOtp: false });
            return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
          }

          const errorMessage = await extractErrorMessage(error);
          set({ isResendingVerificationOtp: false });
          return {
            success: false,
            error: errorMessage || "Failed to resend verification code",
            errorObject: error,
          };
        }

        set({
          isResendingVerificationOtp: false,
          verificationOtpMethod: method,
          verificationOtpSentAt: now,
          verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
          verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
        });

        return {
          success: true,
          message:
            data?.message ??
            data?.data?.message ??
            "If the email address can be used, a verification code has been sent.",
        };
      }

      const result =
        method === "email"
          ? await verificationService.resendEmailVerification()
          : await verificationService.resendPhoneVerification();

      if (result.error) {
        if (await isSessionExpiredCode(result.error)) {
          set({ isResendingVerificationOtp: false });
          return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
        }

        const errorMessage = await extractErrorMessage(result.error);
        set({ isResendingVerificationOtp: false });
        return {
          success: false,
          error: errorMessage || "Failed to resend verification code",
          errorObject: result.error,
        };
      }

      set({
        isResendingVerificationOtp: false,
        verificationOtpMethod: method,
        verificationOtpSentAt: now,
        verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
        verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
        // Carry over verificationRequestId if the resend response provides an updated one
        ...(result.data?.verification_request_id
          ? { verificationRequestId: result.data.verification_request_id }
          : {}),
      });

      return {
        success: true,
        message: result.data?.message ?? "Verification code resent",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to resend verification code";
      set({ isResendingVerificationOtp: false });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  verifyVerificationOtp: async (method, otp): Promise<TVerifyVerificationOtpResult> => {
    const now = Date.now();
    const state = get();
    const expiresAt = state.verificationOtpExpiresAt;

    const trimmed = otp.trim();
    if (!trimmed) {
      return { success: false, error: "OTP is required" };
    }
    if (expiresAt && now > expiresAt) {
      return { success: false, error: "Code expired. Please resend to get a new code." };
    }

    set({
      isVerifyingVerificationOtp: true,
      verificationOtpMethod: method,
    });

    try {
      const result =
        method === "email"
          ? await verificationService.verifyEmail({ otp: trimmed })
          : await verificationService.verifyPhone({ otp: trimmed });

      if (result.error) {
        // Detect SESSION_EXPIRED before doing anything else
        if (await isSessionExpiredCode(result.error)) {
          set({ isVerifyingVerificationOtp: false, ..._clearOtpState() });
          return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
        }

        const errorMessage = await extractErrorMessage(result.error);
        // Increment local counter for warning-banner display (last attempt etc.)
        const attempts = (get().verificationOtpFailedAttempts ?? 0) + 1;
        set({ isVerifyingVerificationOtp: false, verificationOtpFailedAttempts: attempts });

        return {
          success: false,
          attemptsRemaining: undefined, // server might embed this in error detail
          error: errorMessage || "Verification failed",
          errorObject: result.error,
        };
      }

      // Server issued a new OTP after max attempts (reset-on-lockout)
      if (result.data?.otp_reset) {
        set({
          isVerifyingVerificationOtp: false,
          verificationOtpSentAt: now,
          verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
          verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
          verificationOtpFailedAttempts: 0,
          ...(result.data.verification_request_id
            ? { verificationRequestId: result.data.verification_request_id }
            : {}),
        });
        return {
          success: true,
          otpReset: true,
          attemptsRemaining: result.data.attempts_remaining,
          message: result.data.message ?? "Too many wrong codes — a new code has been sent.",
        };
      }

      // Idempotent verify: already verified within 5-min window
      if (result.data?.already_verified) {
        await get().fetchProfile({ force: true });
        set({ isVerifyingVerificationOtp: false, ..._clearOtpState() });
        return {
          success: true,
          verified: true,
          alreadyVerified: true,
          message: result.data.message ?? "Already verified.",
        };
      }

      // Normal success
      const verified = Boolean(result.data?.success && result.data.verified);
      if (verified) {
        await get().fetchProfile({ force: true });
        set({ isVerifyingVerificationOtp: false, ..._clearOtpState() });
        return {
          success: true,
          verified: true,
          attemptsRemaining: result.data?.attempts_remaining,
          message: result.data?.message ?? "Verified successfully",
        };
      }

      // Success response but not verified — treat as a soft failure (shouldn't normally occur)
      const attempts = (get().verificationOtpFailedAttempts ?? 0) + 1;
      set({ isVerifyingVerificationOtp: false, verificationOtpFailedAttempts: attempts });

      return {
        success: Boolean(result.data?.success),
        verified: false,
        attemptsRemaining: result.data?.attempts_remaining,
        message: result.data?.message ?? "Verification failed",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Verification failed";
      const attempts = (get().verificationOtpFailedAttempts ?? 0) + 1;
      set({ isVerifyingVerificationOtp: false, verificationOtpFailedAttempts: attempts });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  changeVerificationEmail: async (newEmail): Promise<TChangeVerificationContactResult> => {
    const trimmed = newEmail.trim();
    if (!trimmed) {
      return { success: false, error: "Email address is required." };
    }

    set({ isChangingVerificationContact: true });

    try {
      const result = await verificationService.changeEmail({ new_email: trimmed });

      if (result.error) {
        if (await isSessionExpiredCode(result.error)) {
          set({ isChangingVerificationContact: false, ..._clearOtpState() });
          return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
        }

        const errorMessage = await extractErrorMessage(result.error);
        set({ isChangingVerificationContact: false });
        return {
          success: false,
          error: errorMessage || "Failed to request email change.",
          errorObject: result.error,
        };
      }

      const contactUpdated = Boolean(result.data?.contact_updated);
      const now = Date.now();

      // Update the store profile and reset OTP timers only when the server confirms the
      // contact was updated (enumeration-safe: same public message regardless of outcome).
      if (contactUpdated) {
        const profile = get().profile;
        if (profile) {
          set({ profile: { ...profile, email: trimmed } });
        }
        set({
          verificationOtpSentAt: now,
          verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
          verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
          verificationOtpFailedAttempts: 0,
          ...(result.data?.verification_request_id
            ? { verificationRequestId: result.data.verification_request_id }
            : {}),
        });
      }

      set({ isChangingVerificationContact: false });
      return {
        success: true,
        contactUpdated,
        message: result.data?.message ?? "If the email can be used, a verification code will be sent.",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to request email change.";
      set({ isChangingVerificationContact: false });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  changeVerificationPhone: async (newPhone): Promise<TChangeVerificationContactResult> => {
    const trimmed = newPhone.trim();
    if (!trimmed) {
      return { success: false, error: "Phone number is required." };
    }

    set({ isChangingVerificationContact: true });

    try {
      const result = await verificationService.changePhone({ new_phone_number: trimmed });

      if (result.error) {
        if (await isSessionExpiredCode(result.error)) {
          set({ isChangingVerificationContact: false, ..._clearOtpState() });
          return { success: false, sessionExpired: true, error: SESSION_EXPIRED_USER_MESSAGE };
        }

        const errorMessage = await extractErrorMessage(result.error);
        set({ isChangingVerificationContact: false });
        return {
          success: false,
          error: errorMessage || "Failed to request phone change.",
          errorObject: result.error,
        };
      }

      const contactUpdated = Boolean(result.data?.contact_updated);
      const now = Date.now();

      if (contactUpdated) {
        const profile = get().profile;
        if (profile) {
          set({ profile: { ...profile, phone_number: trimmed } });
        }
        set({
          verificationOtpSentAt: now,
          verificationOtpExpiresAt: now + OTP_EXPIRY_MS,
          verificationOtpResendAvailableAt: now + RESEND_COOLDOWN_MS,
          verificationOtpFailedAttempts: 0,
          ...(result.data?.verification_request_id
            ? { verificationRequestId: result.data.verification_request_id }
            : {}),
        });
      }

      set({ isChangingVerificationContact: false });
      return {
        success: true,
        contactUpdated,
        message: result.data?.message ?? "If the number can be used, a WhatsApp code will be sent.",
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to request phone change.";
      set({ isChangingVerificationContact: false });
      return { success: false, error: errorMessage, errorObject: err };
    }
  },

  verifyTwoFactorLogin: async (token): Promise<TVerifyTwoFactorLoginResult> => {
    try {
      const trimmed = token.trim();
      if (!trimmed) {
        return { success: false, error: "OTP is required" };
      }

      const { data, error } = await twoFactorService.verifyTwoFactorLogin({ token: trimmed });
      if (error) {
        const errorMessage = await extractErrorMessage(error);
        return { success: false, error: errorMessage || "Verification failed", errorObject: error };
      }

      if (!data) {
        return { success: false, error: "No data returned" };
      }

      const loginData = data.data;

      // Persist tokens (cookies are single source of truth)
      tokenStorage.setAccessToken(loginData.access_token);
      if (loginData.refresh_token?.token) {
        tokenStorage.setRefreshToken(loginData.refresh_token.token);
      }
      const accessTokenExpiration: number | null = getJwtExpiration(loginData.access_token);
      if (accessTokenExpiration !== null) {
        tokenStorage.setExpiresAt(accessTokenExpiration);
      }

      // Update store tokens
      set({
        accessToken: loginData.access_token,
        expiresAt: accessTokenExpiration ?? undefined,
      });

      // Fetch and save user profile (do not fail the login if profile fetch fails)
      const { data: profileData } = await userService.getProfile();
      if (profileData) {
        set({ profile: profileData });
      }

      // Fetch and save user roles (do not fail the login if roles fetch fails)
      const { data: rolesData } = await userService.getRoles();
      if (rolesData) {
        set({ roles: rolesData });
      }

      return { success: true, roles: rolesData ?? null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Verification failed";
      return { success: false, error: errorMessage, errorObject: err };
    }
  },
});
